import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { createPmForm, deletePmForm, getPmFormById, getPmForms, updatePmForm } from '../services/form.service';
import { GetPmFormsParams, PmFormInsertInput, PmFormUpdateInput } from '../types/form.types';

const PM_FORMS_KEY = 'hr-pm-forms';

const useInvalidatePmForms = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [PM_FORMS_KEY] });
};

export const useGetPmForms = (params: GetPmFormsParams) =>
  useGlobalQuery({
    queryKey: [PM_FORMS_KEY, params],
    queryFn: () => getPmForms(params),
    staleTime: 5 * 60 * 1000,
  });

export const useGetPmFormById = (itemId?: string) =>
  useGlobalQuery({
    queryKey: [PM_FORMS_KEY, 'detail', itemId],
    queryFn: () => getPmFormById(itemId as string),
    enabled: Boolean(itemId),
  });

export const useCreatePmForm = () => {
  const invalidate = useInvalidatePmForms();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: PmFormInsertInput) => createPmForm(input),
    onSuccess: () => {
      invalidate();
      toast({ title: t('FORM_CREATED') });
    },
  });
};

export const useUpdatePmForm = () => {
  const invalidate = useInvalidatePmForms();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: PmFormUpdateInput }) => updatePmForm(params),
    onSuccess: () => {
      invalidate();
      toast({ title: t('FORM_UPDATED') });
    },
  });
};

export const useDeletePmForm = () => {
  const invalidate = useInvalidatePmForms();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deletePmForm(itemId),
    onSuccess: () => {
      invalidate();
      toast({ title: t('FORM_DELETED') });
    },
  });
};
